import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from '../utils/logger';
import { tokenManager } from '../utils/tokenManager';
import { clearAllStorage } from '../utils/storageCleanup';
import {
  ServiceConfig,
  ServiceConfigResponse,
  GlobalConfig,
  RuleConfig,
  RuleConfigResponse,
  CloudTrailEvent,
  ResourceInformation,
  TaggingOperation,
  ServiceMapping,
  ScanResultDetail,
  CloudTrailEventsResponse,
} from '../types/tagit';
import {
  NamingConventionListResponse,
  NamingConventionGetResponse,
  NamingConventionConfiguration,
  NamingConventionDefaultResponse,
  NamingConventionAttachmentsResponse,
  NamingConventionAttachmentResponse,
} from '../types/tagops';

// Create axios instance with interceptors
const apiClient = axios.create();

// Add request interceptor to ensure tokens are valid before making requests
apiClient.interceptors.request.use(
  async (config) => {
    console.log('API Request interceptor - checking tokens');
    
    // Check if tokens are expired
    if (tokenManager.areTokensExpired()) {
      console.log('Tokens are expired, attempting to refresh');
      logger.log('Tokens are expired, attempting to refresh');
      
      try {
        // Try to refresh tokens
        await tokenManager.refreshTokens();
        console.log('Tokens refreshed successfully');
        logger.log('Tokens refreshed successfully');
      } catch (error) {
        console.error('Token refresh failed:', error);
        logger.log('Token refresh failed, logging out user');
        
        // Clear all stored tokens and user info
        tokenManager.clearTokens();
        
        // Clear all localStorage, sessionStorage, cookies, and cache
        clearAllStorage();
        
        // Show notification and redirect to Cognito login
        // Dispatch a custom event to trigger the notification
        window.dispatchEvent(new CustomEvent('showLogoutNotification'));
        
        // Redirect to Cognito after a short delay to show notification
        setTimeout(() => {
          const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || '';
          const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
          const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN || 'http://localhost:3000/callback';
          
          const loginUrl = `https://${cognitoDomain}/login?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
          window.location.href = loginUrl;
        }, 3000);
        
        // Cancel the request
        return Promise.reject(new Error('Token refresh failed'));
      }
    } else if (tokenManager.shouldRefreshTokens()) {
      console.log('Tokens are close to expiry, refreshing proactively');
      // If tokens are close to expiry, refresh them proactively
      try {
        await tokenManager.refreshTokens();
        console.log('Tokens refreshed proactively');
        logger.log('Tokens refreshed proactively');
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
        logger.error('Proactive token refresh failed:', error);
        // Continue with the request even if proactive refresh fails
        // The request interceptor will handle it if the token is actually expired
      }
    } else {
      console.log('Tokens are valid, proceeding with request');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 and 403 errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('API Response interceptor - checking for errors');
    
    if (error.response?.status === 401) {
      console.error('401 Unauthorized error detected, logging out user');
      logger.log('401 Unauthorized error detected, logging out user');
      
      // Clear all stored tokens and user info
      tokenManager.clearTokens();
      
      // Clear all localStorage, sessionStorage, cookies, and cache
      clearAllStorage();
      
      // Show notification and redirect to Cognito login
      // Dispatch a custom event to trigger the notification
      window.dispatchEvent(new CustomEvent('showLogoutNotification'));
      
      // Redirect to Cognito after a short delay to show notification
      setTimeout(() => {
        const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || '';
        const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
        const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_SIGN_IN || 'http://localhost:3000/callback';
        
        const loginUrl = `https://${cognitoDomain}/login?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
        window.location.href = loginUrl;
      }, 3000);
    } else if (error.response?.status === 403) {
      console.error('403 Forbidden error detected');
      logger.log('403 Forbidden error detected');
      
      // Extract clear error message from structured error response
      if (error.response.data?.error?.message) {
        error.message = error.response.data.error.message;
      } else if (error.response.data?.message) {
        error.message = error.response.data.message;
      } else if (!error.message) {
        error.message = 'Access Denied';
      }
    } else {
      console.log('API Error (not 401/403):', error.response?.status, error.message);
      
      // For other errors, try to extract clear message from structured response
      if (error.response?.data?.error?.message && !error.message) {
        error.message = error.response.data.error.message;
      } else if (error.response?.data?.message && !error.message) {
        error.message = error.response.data.message;
      }
    }
    return Promise.reject(error);
  }
);

// Update API base URL to point to API Gateway
const API_BASE_URL = process.env.REACT_APP_API_GATEWAY_URL || '';

const getAuthHeaders = () => {
  // Use ID token instead of access token for JWT authentication
  // The ID token is a JWT that contains the 'aud' claim required by the Lambda authorizer
  const idToken = localStorage.getItem('id_token');
  if (idToken) {
    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
  }
  
  // Fallback to access token if ID token is not available
  const accessToken = localStorage.getItem('access_token');
  if (accessToken) {
    logger.warn('Using access token instead of ID token - this may not work with JWT authorizers');
    return {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };
  }
  
  logger.warn('No authentication token found');
  return {
    'Content-Type': 'application/json',
  };
};

class TagopsService {
  private async request<T>(
    endpoint: string,
    options: any = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const authHeaders = getAuthHeaders();
    
    const config = {
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await apiClient(url, config);
      
      // Handle wrapped response format: {"success": true, "data": {...}}
      if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
        if (response.data.success) {
          return response.data.data;
        } else {
          // Extract clear error message from structured error response
          const errorMessage = response.data.error?.message || response.data.message || 'API request failed';
          const error = new Error(errorMessage);
          // Preserve the original response structure for better error handling
          (error as any).response = { data: response.data };
          throw error;
        }
      }
      
      // Handle direct response format
      return response.data;
    } catch (error: any) {
      logger.error('API request failed:', error);
      
      // Let the axios interceptor handle 401 errors
      throw error;
    }
  }

  // Configuration Management
  async getServiceConfig(): Promise<ServiceConfigResponse> {
    return this.request<ServiceConfigResponse>('/config/services');
  }

  async updateServiceConfig(config: Record<string, ServiceConfig[]>): Promise<void> {
    return this.request<void>('/config/services', {
      method: 'PUT',
      data: config,
    });
  }

  async getGlobalConfig(): Promise<GlobalConfig> {
    return this.request<GlobalConfig>('/config/global');
  }

  async updateGlobalConfig(config: GlobalConfig): Promise<void> {
    return this.request<void>('/config/global', {
      method: 'PUT',
      data: config,
    });
  }

  // Rule Management
  async getRuleConfig(): Promise<RuleConfigResponse> {
    return this.request<RuleConfigResponse>('/rules');
  }

  async updateRuleConfig(config: RuleConfig): Promise<void> {
    return this.request<void>('/rules', {
      method: 'PUT',
      data: config,
    });
  }

  // Event Processing
  async processEvent(event: CloudTrailEvent): Promise<{
    resourceInfo: ResourceInformation;
    appliedTags: Array<Record<string, string>>;
    status: 'success' | 'failed';
    error?: string;
  }> {
    return this.request('/events/process', {
      method: 'POST',
      data: event,
    });
  }

  async testEvent(event: CloudTrailEvent): Promise<{
    resourceInfo: ResourceInformation;
    appliedTags: Array<Record<string, string>>;
    preview: boolean;
  }> {
    return this.request('/events/test', {
      method: 'POST',
      data: event,
    });
  }

  // Resource Scanning - Consolidated API
  async scanResources(service: string, resourceType: string, region: string): Promise<ResourceInformation[]> {
    return this.request<ResourceInformation[]>(`/scan`, {
      method: 'POST',
      data: { 
        scan_type: 'single',
        service, 
        resource_type: resourceType, 
        region 
      },
    });
  }

  async scanResourcesDetailed(service: string, resourceType: string, region: string, dryRun: boolean = false, signal?: AbortSignal): Promise<any> {
    return this.request<any>(`/scan`, {
      method: 'POST',
      data: { 
        scan_type: 'single',
        service, 
        resource_type: resourceType, 
        region,
        ...(dryRun && { dry_run: true })
      },
      signal,
    });
  }

  async bulkScanResources(service: string, resourceTypes: string[], region: string): Promise<Record<string, ScanResultDetail>> {
    return this.request<Record<string, ScanResultDetail>>('/scan', {
      method: 'POST',
      data: { 
        scan_type: 'bulk',
        service, 
        resource_types: resourceTypes, 
        region 
      },
    });
  }

  async bulkScanResourcesPreview(service: string, resourceTypes: string[], region: string): Promise<Record<string, ScanResultDetail>> {
    return this.request<Record<string, ScanResultDetail>>('/scan', {
      method: 'POST',
      data: { 
        scan_type: 'bulk',
        service, 
        resource_types: resourceTypes, 
        region, 
        dry_run: true 
      },
    });
  }

  // Full Account Scan
  async fullAccountScan(region: string): Promise<Record<string, Record<string, ScanResultDetail>>> {
    return this.request<Record<string, Record<string, ScanResultDetail>>>('/scan', {
      method: 'POST',
      data: { 
        scan_type: 'full_account',
        region 
      },
    });
  }

  async fullAccountScanPreview(region: string): Promise<Record<string, Record<string, ScanResultDetail>>> {
    return this.request<Record<string, Record<string, ScanResultDetail>>>('/scan', {
      method: 'POST',
      data: { 
        scan_type: 'full_account',
        region, 
        dry_run: true 
      },
    });
  }

  // Health Check
  async healthCheck(): Promise<{ status: string; version: string }> {
    return this.request<{ status: string; version: string }>('/health');
  }

  // Sample Events
  async getSampleEvents(): Promise<Record<string, CloudTrailEvent>> {
    return this.request<Record<string, CloudTrailEvent>>('/events/samples');
  }

  // CloudTrail Events
  async getCloudTrailEvents(region: string = 'us-east-1', eventName: string): Promise<CloudTrailEventsResponse> {
    return this.request<CloudTrailEventsResponse>(`/events/cloudtrail`, {
      params: { region, event_name: eventName },
    });
  }

  // Mock API Testing
  async testMockApi(): Promise<any> {
    return this.request<any>('/echo');
  }

  // AWS Account Management
  async getAccounts(): Promise<any> {
    return this.request('/account/list');
  }

  async getAccountCount(): Promise<{
    message: string;
    count: number;
  }> {
    const result = await this.request<{accounts: any[]}>('/account/list');
    return {
      message: 'Account count retrieved successfully',
      count: result.accounts ? result.accounts.length : 0
    };
  }

  async createAccount(accountData: {
    accountId: string;
    accountName: string;
    roleName: string;
    externalId: string;
    organizationId?: string;
  }) {
    return this.request('/account', {
      method: 'POST',
      data: accountData,
    });
  }

  async updateAccount(accountData: {
    accountId: string;
    accountName: string;
    roleName: string;
    externalId: string;
  }) {
    return this.request('/account', {
      method: 'PUT',
      data: accountData,
    });
  }

  async deleteAccount(accountData: {
    accountId: string;
  }) {
    return this.request('/account', {
      method: 'DELETE',
      data: accountData,
    });
  }

  async verifyAccount(accountId: string, roleName: string, externalId: string) {
    return this.request(`/account/verify?accountId=${accountId}&roleName=${roleName}&externalId=${externalId}`);
  }

  // Scan Settings Management
  async getScanSettings(): Promise<any> {
    return this.request('/settings/scan');
  }

  async updateScanSettings(scanData: {
    status: string;
    cron: string;
    timezone: string;
    offset: number;
  }) {
    return this.request('/settings/scan', {
      method: 'PUT',
      data: scanData,
    });
  }

  /** Scan limit notifications (resources auto-excluded from scanning due to volume). */
  async getScanEvents(): Promise<{
    message: string;
    data: {
      scan_limits: Array<{
        accountId: string | null;
        resource: string | null;
        observed_count?: string | number;
      }>;
    };
  }> {
    return this.request('/settings/scan_events');
  }

  async acknowledgeScanEvent(payload: { accountId: string; resource: string }): Promise<{
    message: string;
    data: {
      deleted: boolean;
      accountId: string;
      resource: string;
      type?: string;
    };
  }> {
    return this.request('/settings/scan_events', {
      method: 'DELETE',
      data: payload,
    });
  }

  // Account Settings - Allowed Services
  async getSupportedServices(): Promise<Record<string, string[]>> {
    const result: any = await this.request<any>('/settings/supported-services');
    // Normalize nested { message, data } shape
    const payload = result && typeof result === 'object' && 'data' in result ? result.data : result;
    return payload as Record<string, string[]>;
  }

  async getAccountServiceSettings(accountId: string): Promise<{
    message?: string;
    allowed_resources: string[];
    denied_resources: string[];
  }> {
    const result: any = await this.request<any>('/settings/service', {
      params: { accountId },
    });
    const payload = result && typeof result === 'object' && 'data' in result ? result.data : result;
    return {
      message: payload?.message,
      allowed_resources: Array.isArray(payload?.allowed_resources) ? payload.allowed_resources : [],
      denied_resources: Array.isArray(payload?.denied_resources) ? payload.denied_resources : [],
    };
  }

  async updateAccountServiceSettings(payload: {
    accountId: string;
    data: {
      allowed_resources: string[];
      denied_resources: string[];
    };
  }): Promise<void> {
    return this.request<void>('/settings/service', {
      method: 'PUT',
      data: payload,
    });
  }

  // Account Allowed Regions
  async updateAccountAllowedRegions(payload: {
    accountId: string;
    allowed_regions: string[];
  }): Promise<void> {
    return this.request<void>('/settings/account', {
      method: 'PUT',
      data: payload,
    });
  }

  // User Management
  async addUser(userData: {
    email: string;
    firstName: string;
    lastName: string;
    role: 'Administrator' | 'PowerUser' | 'ReadOnly';
  }): Promise<{
    message: string;
    userId: string;
    tempPassword: string;
    role: string;
  }> {
    return this.request('/users/add', {
      method: 'POST',
      data: userData,
    });
  }

  async updateUsers(updates: Array<{
    userId: string;
    firstName?: string;
    lastName?: string;
    role?: 'Administrator' | 'PowerUser' | 'ReadOnly';
    status?: 'ENABLED' | 'DISABLED';
  }>): Promise<{
    message: string;
    updatedUsers: string[];
  }> {
    return this.request('/users/modify', {
      method: 'POST',
      data: { updates },
    });
  }

  async deleteUsers(userIds: string[]): Promise<{
    message: string;
    deletedUsers: string[];
  }> {
    return this.request('/users/delete', {
      method: 'POST',
      data: { userIds },
    });
  }

  async listUsers(filters?: {
    role?: 'Administrator' | 'PowerUser' | 'ReadOnly';
    status?: string;
    search?: string;
  }): Promise<{
    users: Array<{
      userName: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string; // Lambda now returns 'role' directly in frontend format
      status: string;
      createdAt: string;
      updatedAt: string;
      lastLogin?: string;
    }>;
    totalCount: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    
    return this.request(`/users/list?${params.toString()}`);
  }

  // Inventory
  async getInventory(): Promise<{
    resources: Array<{
      id: string;
      name: string;
      type: string;
      service: string;
      region: string;
      account: string;
      tags: Record<string, string>;
      status: string;
      lastModified: string;
      arn: string;
      creation_time: string;
      username: string;
    }>;
    total_count: number;
    service_stats: Record<string, number>;
  }> {
    return this.request('/inventory');
  }

  // Manual Tag Operations
  async addTagsManually(
    resourceArns: string[], 
    tags: Record<string, string>,
    resourceRegions?: Record<string, string>
  ): Promise<{
    message: string;
    total_resources: number;
    successful: number;
    failed: number;
    results: Array<{
      arn: string;
      status: 'success' | 'failed';
      message: string;
    }>;
    errors?: Array<{
      arn: string;
      error: string;
    }>;
  }> {
    const requestData: any = {
      operation: 'add',
      resource_arns: resourceArns,
      tags: tags,
    };
    
    if (resourceRegions && Object.keys(resourceRegions).length > 0) {
      requestData.resource_regions = resourceRegions;
    }
    
    return this.request('/inventory/manual-tags', {
      method: 'POST',
      data: requestData,
    });
  }

  async deleteTagsManually(
    resourceArns: string[], 
    tagKeys: string[],
    resourceRegions?: Record<string, string>
  ): Promise<{
    message: string;
    total_resources: number;
    successful: number;
    failed: number;
    results: Array<{
      arn: string;
      status: 'success' | 'failed';
      message: string;
    }>;
    errors?: Array<{
      arn: string;
      error: string;
    }>;
  }> {
    const requestData: any = {
      operation: 'delete',
      resource_arns: resourceArns,
      tag_keys: tagKeys,
    };
    
    if (resourceRegions && Object.keys(resourceRegions).length > 0) {
      requestData.resource_regions = resourceRegions;
    }
    
    return this.request('/inventory/manual-tags', {
      method: 'POST',
      data: requestData,
    });
  }

  async getSubscriptionStatus(): Promise<{
    message: string;
    subscription: {
      tenant_id: string;
      subscription_id: string;
      plan_id: string; // 'aws' | 'fixed'
      status: string;
      created_at: string;
      activated_at?: string;
      tier_type?: string;
      aws_account_count?: number | string;
      quantity?: number | string;
      is_free_trial_offer?: boolean;
    } | null;
    trial_end_date?: string;
    currency_context?: {
      effective_currency: string;
      preferred_currency?: string | null;
      country_detected?: string | null;
      has_currency_override: boolean;
      available_currencies: string[];
    };
  }> {
    return this.request('/subscription/status');
  }

  // Timeline data for resource count over time
  async getTimeline(): Promise<{
    message: string;
    data: string; // CSV data
  }> {
    return this.request('/settings/timeline');
  }

  // Get service count settings
  async getCountSettings(): Promise<{
    message: string;
    data: Record<string, string>;
  }> {
    const result: any = await this.request<any>('/settings/count');
    // After this.request() unwraps, result is {message: "...", data: {...}}
    return {
      message: result?.message || '',
      data: result?.data || {},
    };
  }

  // AWS Organization Management
  async getOrganizations(): Promise<{
    organizations: Array<{
      tenantId: string;
      type: string;
      status: string;
      organization_name: string;
      role_name: string;
      externalId: string;
      cloudformation_arn?: string;
      onboarding_region?: string;
      organizationManagementAccountId?: string;
    }>;
  }> {
    return this.request('/organization/list');
  }

  async createOrganization(organizationData: {
    accountId: string;
    organizationName: string;
    roleName: string;
    externalId: string;
    organizationalUnitIds?: string;
    includeManagementAccount?: string;
  }) {
    return this.request('/organization', {
      method: 'POST',
      data: organizationData,
    });
  }

  async updateOrganization(organizationData: {
    accountId: string;
    organizationName: string;
    roleName: string;
    externalId: string;
    organizationalUnitIds?: string;
    includeManagementAccount?: string;
  }) {
    return this.request('/organization', {
      method: 'PUT',
      data: organizationData,
    });
  }

  async deleteOrganization(organizationData: {
    accountId: string;
  }) {
    return this.request('/organization', {
      method: 'DELETE',
      data: organizationData,
    });
  }

  async verifyOrganization(accountId: string, roleName: string, externalId: string) {
    // Remove "-org" suffix from roleName if present, backend will add it back
    const cleanRoleName = roleName.endsWith('-org') ? roleName.slice(0, -4) : roleName;
    return this.request(`/organization/verify?accountId=${accountId}&roleName=${cleanRoleName}&externalId=${externalId}`);
  }

  async getOrganizationMembers(accountId: string): Promise<{
    message: string;
    members: string[];
  }> {
    return this.request(`/organization/members?accountId=${accountId}`);
  }

  // Naming Convention Management
  async listNamingConventions(): Promise<NamingConventionListResponse> {
    return this.request<NamingConventionListResponse>('/naming-convention/list');
  }

  async getNamingConvention(ruleName: string): Promise<NamingConventionGetResponse> {
    return this.request<NamingConventionGetResponse>(`/naming-convention?ruleName=${encodeURIComponent(ruleName)}`);
  }

  async createNamingConvention(ruleName: string, ruleConfiguration: NamingConventionConfiguration): Promise<{
    message: string;
  }> {
    return this.request<{ message: string }>('/naming-convention', {
      method: 'POST',
      data: {
        ruleName,
        ruleConfiguration,
      },
    });
  }

  async updateNamingConvention(ruleName: string, ruleConfiguration: NamingConventionConfiguration): Promise<{
    message: string;
  }> {
    return this.request<{ message: string }>('/naming-convention', {
      method: 'PUT',
      data: {
        ruleName,
        ruleConfiguration,
      },
    });
  }

  async deleteNamingConvention(ruleName: string): Promise<{
    message: string;
  }> {
    return this.request<{ message: string }>('/naming-convention', {
      method: 'DELETE',
      data: {
        ruleName,
      },
    });
  }

  async getDefaultNamingConvention(): Promise<NamingConventionDefaultResponse> {
    return this.request<NamingConventionDefaultResponse>('/naming-convention/default');
  }

  async listNamingConventionAttachments(): Promise<NamingConventionAttachmentsResponse> {
    return this.request<NamingConventionAttachmentsResponse>('/naming-convention/attach/list');
  }

  async attachNamingConventionToAccount(ruleName: string, accountId: string): Promise<{
    message: string;
  }> {
    return this.request<{ message: string }>('/naming-convention/attach', {
      method: 'POST',
      data: {
        ruleName,
        accountId,
      },
    });
  }

  async detachNamingConventionFromAccount(accountId: string): Promise<{
    message: string;
  }> {
    return this.request<{ message: string }>('/naming-convention/attach', {
      method: 'DELETE',
      data: {
        accountId,
      },
    });
  }

  async getNamingConventionForAccount(accountId: string): Promise<NamingConventionAttachmentResponse> {
    return this.request<NamingConventionAttachmentResponse>(`/naming-convention/attach?accountId=${accountId}`);
  }

  // Naming Convention Acknowledgment
  async acknowledgeNamingConvention(arn: string): Promise<{ message: string }> {
    return this.request('/naming-convention/ack', {
      method: 'POST',
      data: { arn },
    });
  }

  async deleteNamingConventionAcknowledgment(arn: string): Promise<{ message: string }> {
    return this.request('/naming-convention/ack', {
      method: 'DELETE',
      data: { arn },
    });
  }

  // Naming Convention Recalculation
  async recalculateNamingConvention(): Promise<{ message: string }> {
    return this.request<{ message: string }>('/naming-convention/recalculate');
  }

  // API Tokens Management
  async listApiTokens(): Promise<{
    message: string;
    tokens: Array<{
      isActive: boolean;
      expiresAt: string;
      createdAt: string;
      scope: 'read' | 'write';
      name: string;
    }>;
  }> {
    return this.request('/tokens/list');
  }

  async createApiToken(data: {
    name: string;
    expiresAt: string;
    scope: 'read' | 'write';
  }): Promise<{
    message: string;
    token: string;
    name: string;
    expiresAt: number;
    scope: 'read' | 'write';
  }> {
    // Convert expiresAt from string to integer for backend validation
    const payload = {
      ...data,
      expiresAt: parseInt(data.expiresAt, 10),
    };
    return this.request('/tokens', {
      method: 'POST',
      data: payload,
    });
  }

  async deleteApiToken(name: string): Promise<{
    message: string;
  }> {
    return this.request('/tokens', {
      method: 'DELETE',
      data: { name },
    });
  }

  async activateApiToken(name: string): Promise<{
    message: string;
  }> {
    return this.request('/tokens/activate', {
      method: 'PUT',
      data: { name },
    });
  }

  async deactivateApiToken(name: string): Promise<{
    message: string;
  }> {
    return this.request('/tokens/deactivate', {
      method: 'PUT',
      data: { name },
    });
  }

}

export const tagopsService = new TagopsService(); 